"use client";

import { useCallback } from "react";
import type { Address } from "viem";
import { usePublicClient, useWalletClient } from "wagmi";

import { ERC20_ABI } from "@/data/contracts/abis";

/**
 * One reset-then-approve implementation, replacing the five independent
 * copies in dotone-swap-v3 (Swap.tsx, Liquidity.tsx, PoolItem.tsx,
 * V3Swap.tsx, V3Liquidity.tsx). Some ERC20s revert on approve() when the
 * current allowance is already nonzero, so bump to 0 first if needed.
 * No-ops if the current allowance already covers the amount.
 */
export function useEnsureAllowance() {
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();

  return useCallback(
    async (token: Address, spender: Address, amount: bigint, owner: Address, onApprove?: () => void) => {
      if (!walletClient || !publicClient) throw new Error("Wallet not connected");

      const current = await publicClient.readContract({
        address: token,
        abi: ERC20_ABI,
        functionName: "allowance",
        args: [owner, spender],
      });
      if (current >= amount) return;
      onApprove?.();

      if (current > 0n) {
        const resetHash = await walletClient.writeContract({
          address: token,
          abi: ERC20_ABI,
          functionName: "approve",
          args: [spender, 0n],
        });
        await publicClient.waitForTransactionReceipt({ hash: resetHash });
      }

      const hash = await walletClient.writeContract({
        address: token,
        abi: ERC20_ABI,
        functionName: "approve",
        args: [spender, amount],
      });
      await publicClient.waitForTransactionReceipt({ hash });
    },
    [walletClient, publicClient],
  );
}
